import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Send, MessageCircle, MessageSquare, Share2, X as XIcon, Eye, Edit2, Facebook } from "lucide-react";
import { Deck, ShareAccess } from "@/types/linguadeck";
import { shareDeck, updateShareAccess, unshareDeck } from "@/lib/db";

interface ShareModalProps {
  deck: Deck;
  onClose: () => void;
  onShared: (deckId: string, token: string | null, shareAccess?: ShareAccess) => void;
}

export default function ShareModal({ deck, onClose, onShared }: ShareModalProps) {
  const { t } = useTranslation();
  const [accessLevel, setAccessLevel] = useState<ShareAccess>(
    (deck.shareAccess as ShareAccess) ?? "read"
  );
  const [token, setToken] = useState<string | null>(deck.shareToken ?? null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shareUrl = token
    ? `${window.location.origin}/share/${token}`
    : "";

  const handleGenerateOrUpdate = async () => {
    setLoading(true);
    setError(null);
    try {
      if (deck.shareToken && (deck.shareAccess ?? "read") !== accessLevel) {
        await updateShareAccess(deck.id, accessLevel);
        setToken(deck.shareToken);
        onShared(deck.id, deck.shareToken, accessLevel);
      } else {
        const newToken = await shareDeck(deck.id, accessLevel);
        setToken(newToken);
        onShared(deck.id, newToken, accessLevel);
      }
    } catch (e) {
      console.error("Share failed:", e);
      setError(t("shareModalExt.createError"));
    } finally {
      setLoading(false);
    }
  };

  const handleUnshare = async () => {
    setLoading(true);
    setError(null);
    try {
      await unshareDeck(deck.id);
      setToken(null);
      onShared(deck.id, null);
    } catch (e) {
      console.error("Unshare failed:", e);
      setError(t("shareModalExt.unshareError"));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setError(t("shareModalExt.copyError"));
    }
  };

  // If deck already has a token, we may need to update share_access when user changes the toggle
  const needsUpdate =
    deck.shareToken &&
    (deck.shareAccess ?? "read") !== accessLevel;

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: 16,
        backdropFilter: "blur(6px)",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="modal-box"
        style={{
          background: "hsl(var(--bg2))",
          border: "1px solid hsl(var(--border))",
          borderRadius: "var(--radius)",
          padding: 28,
          width: "100%",
          maxWidth: 440,
          maxHeight: "92vh",
          overflowY: "auto",
          boxShadow: "0 24px 64px rgba(0,0,0,.5)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: "linear-gradient(135deg, rgba(0,229,160,.2), rgba(109,90,255,.2))",
                border: "1px solid rgba(0,229,160,.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 24,
              }}
            >
              🔗
            </div>
            <div>
              <div
                style={{
                  fontFamily: "Instrument Serif, serif",
                  fontSize: 20,
                  fontWeight: 600,
                  color: "hsl(var(--foreground))",
                }}
              >
                {t("share_deck")}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "hsl(var(--muted-foreground))",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 2,
                }}
              >
                <span>{deck.emoji || "📚"}</span>
                <span>{deck.title}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "hsl(var(--muted-foreground))",
              cursor: "pointer",
              fontSize: 20,
              padding: 4,
            }}
            aria-label={t("shareModalExt.close")}
          >
            ✕
          </button>
        </div>

        {/* Access level toggle */}
        <div style={{ marginBottom: 20 }}>
          <label
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "hsl(var(--muted-foreground))",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 8,
              display: "block",
            }}
          >
            {t("shareModalExt.accessLevel")}
          </label>
          <div
            style={{
              display: "flex",
              gap: 0,
              background: "hsl(var(--bg3))",
              borderRadius: "var(--radius-sm)",
              padding: 4,
              border: "1px solid hsl(var(--border))",
            }}
          >
            <button
              onClick={() => setAccessLevel("read")}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "10px 16px",
                borderRadius: "calc(var(--radius-sm) - 4px)",
                border: "none",
                background: accessLevel === "read" ? "hsl(var(--primary))" : "transparent",
                color: accessLevel === "read" ? "white" : "hsl(var(--muted-foreground))",
                fontFamily: "DM Sans, sans-serif",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                transition: "all .2s",
              }}
            >
              <Eye className="w-4 h-4 shrink-0" aria-hidden />
              {t("shareModal.readOnly")}
            </button>
            <button
              onClick={() => setAccessLevel("write")}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "10px 16px",
                borderRadius: "calc(var(--radius-sm) - 4px)",
                border: "none",
                background: accessLevel === "write" ? "hsl(var(--primary))" : "transparent",
                color: accessLevel === "write" ? "white" : "hsl(var(--muted-foreground))",
                fontFamily: "DM Sans, sans-serif",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                transition: "all .2s",
              }}
            >
              <Edit2 className="w-4 h-4 shrink-0" aria-hidden />
              {t("shareModal.fullAccess")}
            </button>
          </div>
        </div>

        {/* Generate / Update button when no token or access changed */}
        {(!token || needsUpdate) && (
          <button
            onClick={handleGenerateOrUpdate}
            disabled={loading}
            style={{
              width: "100%",
              padding: "12px 20px",
              borderRadius: 50,
              border: "none",
              background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent2)))",
              color: "white",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 14,
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
              marginBottom: 20,
              boxShadow: "0 4px 16px rgba(109,90,255,.25)",
              transition: "all .2s",
            }}
          >
            {loading ? t("shareModalExt.creatingLink") : token ? t("shareModalExt.updateAccess") : t("shareModalExt.createLink")}
          </button>
        )}

        {/* Share link display */}
        {token && !needsUpdate && (
          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "hsl(var(--muted-foreground))",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 8,
                display: "block",
              }}
            >
              {t("shareModalExt.linkLabel")}
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                readOnly
                value={shareUrl}
                style={{
                  flex: 1,
                  padding: "12px 16px",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid hsl(var(--border))",
                  background: "hsl(var(--bg3))",
                  color: "hsl(var(--foreground))",
                  fontFamily: "JetBrains Mono, monospace",
                  fontSize: 12,
                }}
              />
              <button
                onClick={handleCopy}
                style={{
                  padding: "12px 20px",
                  borderRadius: 50,
                  border: copied
                    ? "1px solid hsl(var(--accent2))"
                    : "1px solid hsl(var(--border))",
                  background: copied ? "rgba(0,229,160,.15)" : "hsl(var(--bg3))",
                  color: copied ? "hsl(var(--accent2))" : "hsl(var(--foreground))",
                  fontFamily: "DM Sans, sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all .2s",
                }}
              >
                {copied ? t("shareModalExt.linkCopied") : t("copy_link")}
              </button>
            </div>
            {/* Social sharing buttons */}
            <div className="flex gap-2 mt-3 flex-wrap">
              <a
                href={`mailto:?subject=${encodeURIComponent(deck.title)}&body=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-[12px] font-medium hover:bg-[hsl(var(--border))]/30 transition-colors"
                title={t("share_email")}
              >
                <Mail size={14} className="shrink-0" />
                {t("share_email")}
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(deck.title)}&url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-[12px] font-medium hover:bg-[hsl(var(--border))]/30 transition-colors"
                title={t("share_twitter")}
              >
                <XIcon size={14} className="shrink-0" strokeWidth={2.5} />
                {t("share_twitter")}
              </a>
              <a
                href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(deck.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-[12px] font-medium hover:bg-[hsl(var(--border))]/30 transition-colors"
                title={t("share_telegram")}
              >
                <Send size={14} className="shrink-0" />
                {t("share_telegram")}
              </a>
              <a
                href={`viber://forward?text=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-[12px] font-medium hover:bg-[hsl(var(--border))]/30 transition-colors"
                title={t("viber")}
              >
                <MessageCircle size={14} className="shrink-0" />
                {t("viber")}
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-[12px] font-medium hover:bg-[hsl(var(--border))]/30 transition-colors"
                title={t("share_facebook")}
              >
                <Facebook size={14} className="shrink-0" />
                {t("share_facebook")}
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(deck.title + " " + shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-[12px] font-medium hover:bg-[hsl(var(--border))]/30 transition-colors"
                title={t("share_whatsapp")}
              >
                <MessageSquare size={14} className="shrink-0" />
                {t("share_whatsapp")}
              </a>
            </div>
          </div>
        )}

        {error && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: "var(--radius-sm)",
              background: "rgba(255,90,138,.1)",
              border: "1px solid rgba(255,90,138,.3)",
              color: "hsl(var(--accent3))",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {/* Unshare button - when deck is shared */}
        {token && (
          <button
            onClick={handleUnshare}
            disabled={loading}
            style={{
              width: "100%",
              padding: "8px 16px",
              borderRadius: 50,
              border: "1px solid rgba(255,90,138,.3)",
              background: "rgba(255,90,138,.06)",
              color: "hsl(var(--accent3))",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 12,
              fontWeight: 500,
              cursor: loading ? "not-allowed" : "pointer",
              marginBottom: 12,
              transition: "all .2s",
            }}
          >
            {t("shareModalExt.unshare")}
          </button>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            width: "100%",
            padding: "10px 20px",
            borderRadius: 50,
            border: "1px solid hsl(var(--border))",
            background: "transparent",
            color: "hsl(var(--muted-foreground))",
            fontFamily: "DM Sans, sans-serif",
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
            transition: "all .2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "hsl(var(--primary))";
            e.currentTarget.style.color = "hsl(var(--foreground))";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "hsl(var(--border))";
            e.currentTarget.style.color = "hsl(var(--muted-foreground))";
          }}
        >
          {t("shareModalExt.close")}
        </button>
      </div>
    </div>
  );
}
